/**
 * Rate Plan Control — dashboard port of the toolkit module (homerunner-toolkit
 * `includes/RatePlanControl/{Matcher,Overrides}.php`; rename + explainer
 * 3.235.0, hide-on-coupon + guest note 3.258.0 / 3.259.0). #1690.
 *
 * Operator rows match a rate plan by its PMS title and can: rename it, attach
 * a hover explainer, and hide it once a coupon is honored (with an optional
 * guest-facing note). Pure functions; BookingForm wires them in.
 *
 * Opt-in like feeLines: `resolveRatePlanControl()` returns null (feature off,
 * the quote renders exactly as before) unless at least one row has match text.
 *
 * Deliberate trims vs the toolkit (disclosed in the PR): no display-order /
 * default-selection (Reorder), no per-plan icon, no WP admin page; the
 * "coupon applied" signal is the widget's own honored-coupon check, not the
 * quote's `coupons` echo (several PMS echo a rejected code back).
 */
import type { BookingWidgetConfig } from './config'

export const MATCH_TYPES = ['contains', 'startsWith', 'exact'] as const
export type MatchType = (typeof MATCH_TYPES)[number]

/** Toolkit Overrides::default_note(), `{remaining}`/`{hidden}` placeholders kept. */
export const DEFAULT_HIDDEN_NOTE_SINGULAR =
  'Promo codes apply to the {remaining} rate. The {hidden} discount cannot be combined with a promo code.'
export const DEFAULT_HIDDEN_NOTE_PLURAL =
  'Promo codes apply to the {remaining} rates. The {hidden} discount cannot be combined with a promo code.'

export interface RatePlanRule {
  match: string
  matchType: MatchType
  display: string
  tooltip: string
  hideWithCoupon: boolean
  hiddenNoteEnabled: boolean
  hiddenNote: string
}

export interface RatePlanLike { id: string; title: string }
export type ControlledRatePlan<T extends RatePlanLike> = T & { tooltip?: string }

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/** Toolkit Matcher::matches. Case-insensitive, against the ORIGINAL PMS title. */
export function matchesRatePlan(plan: RatePlanLike, search: string, matchType: MatchType): boolean {
  const title = String(plan.title ?? '').toLowerCase()
  const needle = String(search ?? '').trim().toLowerCase()
  if (!title || !needle) return false
  switch (matchType) {
    case 'exact':
      return title === needle
    case 'startsWith':
      return title.startsWith(needle)
    default: {
      if (!title.includes(needle)) return false
      // A bare "refundable" must not match a non-refundable plan in any spelling.
      if (!needle.startsWith('non')) {
        const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        if (new RegExp(`non[-\\s]*${escaped}`, 'u').test(title)) return false
      }
      return true
    }
  }
}

const lenientBool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v
    : v === '1' || v === 1 || v === 'true' ? true
      : v === '0' || v === 0 || v === 'false' ? false
        : fallback

/** Raw settings -> normalized rows, or null when nothing is configured. */
export function resolveRatePlanControl(
  options: Pick<BookingWidgetConfig, 'ratePlanControl'>,
): RatePlanRule[] | null {
  const raw = options.ratePlanControl?.rules
  if (!Array.isArray(raw)) return null
  const rules: RatePlanRule[] = []
  for (const row of raw) {
    const match = str(row?.match)
    if (!match) continue
    rules.push({
      match,
      matchType: (MATCH_TYPES as readonly string[]).includes(row?.matchType as string) ? (row!.matchType as MatchType) : 'contains',
      display: str(row?.display),
      tooltip: str(row?.tooltip),
      hideWithCoupon: lenientBool(row?.hideWithCoupon, false),
      hiddenNoteEnabled: lenientBool(row?.hiddenNoteEnabled, true),
      hiddenNote: str(row?.hiddenNote),
    })
  }
  return rules.length ? rules : null
}

const findRule = (plan: RatePlanLike, rules: RatePlanRule[]): RatePlanRule | undefined =>
  rules.find(r => matchesRatePlan(plan, r.match, r.matchType))

function joinTitles(titles: string[]): string {
  const t = titles.filter(Boolean)
  if (t.length <= 1) return t[0] ?? ''
  return `${t.slice(0, -1).join(', ')} and ${t[t.length - 1]}`
}

/**
 * Apply rows to a quote's plans. Hide runs before rename (rows match the PMS
 * title). If every plan would be hidden, none is. Never mutates its input.
 */
export function applyRatePlanControl<T extends RatePlanLike>(
  plans: T[],
  rules: RatePlanRule[],
  couponApplied: boolean,
): { plans: ControlledRatePlan<T>[]; note: string } {
  let kept = plans.map(p => ({ plan: p, rule: findRule(p, rules) }))
  let hidden: typeof kept = []

  if (couponApplied) {
    const stay = kept.filter(e => !e.rule?.hideWithCoupon)
    if (stay.length && stay.length < kept.length) {
      hidden = kept.filter(e => e.rule?.hideWithCoupon)
      kept = stay
    }
  }

  const out = kept.map(({ plan, rule }): ControlledRatePlan<T> => {
    const next: ControlledRatePlan<T> = { ...plan }
    if (rule?.display) next.title = rule.display
    if (rule?.tooltip) next.tooltip = rule.tooltip
    return next
  })

  let note = ''
  if (hidden.length) {
    const noteRule = hidden.map(h => h.rule!).find(r => r.hiddenNoteEnabled)
    if (noteRule) {
      const template = noteRule.hiddenNote
        || (out.length > 1 ? DEFAULT_HIDDEN_NOTE_PLURAL : DEFAULT_HIDDEN_NOTE_SINGULAR)
      const hiddenTitles = hidden.map(h => h.rule!.display || h.plan.title)
      note = template
        .split('{hidden}').join(joinTitles(hiddenTitles))
        .split('{remaining}').join(joinTitles(out.map(p => p.title)))
    }
  }
  return { plans: out, note }
}
